import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from "react";

import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BlockIcon from "@material-ui/icons/Block";
import AddIcon from "@material-ui/icons/Add";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import ListIcon from "@material-ui/icons/List";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import InputBase from "@material-ui/core/InputBase";
import { 
  Box, 
  Card, 
  CardContent, 
  Avatar, 
  Divider, 
  Tooltip, 
  Stack, 
  Typography,
  Grid
} from "@mui/material";
import { WhatsApp, Email } from "@material-ui/icons";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListItemModal from "../../components/ContactListItemModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import useContactLists from "../../hooks/useContactLists";

import planilhaExemplo from "../../assets/planilha.xlsx";
import ForbiddenPage from "../../components/ForbiddenPage";
// import { SocketContext } from "../../context/Socket/SocketContext";


const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];
    const newState = [...state];

    contacts.forEach((contact) => {
      const contactIndex = newState.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        newState[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...newState, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      const newState = [...state];
      newState[contactIndex] = contact;
      return newState;
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      const newState = [...state];
      newState.splice(contactIndex, 1);
      return newState;
    }
    return state;
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    backgroundColor: "transparent",
    border: "none",
  },
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: theme.palette.type === 'light' ? '#f5f5f5' : theme.palette.background.default,
    padding: theme.spacing(4),
    overflow: "hidden",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontWeight: 800,
    color: theme.palette.primary.main,
    fontSize: "2.125rem",
    lineHeight: 1.235,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "50px",
    padding: "6px 20px",
    width: "300px",
    boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.2)",
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s ease",
    "&:focus-within": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      borderColor: theme.palette.primary.main,
    },
  },
  searchInput: {
    marginLeft: theme.spacing(1),
    flex: 1,
    fontSize: "0.95rem",
  },
  searchIcon: {
    color: theme.palette.text.secondary,
  },
  addButton: {
    borderRadius: "50px",
    padding: "8px 24px",
    fontWeight: "bold",
    textTransform: "none",
    boxShadow: theme.shadows[3],
    "&:hover": {
      boxShadow: theme.shadows[6],
    },
  },
}));

const ContactListItems = () => {
  const classes = useStyles();

  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const { contactListId } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactListItemModalOpen, setContactListItemModalOpen] =
    useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [contactList, setContactList] = useState({});
  const fileUploadRef = useRef(null);

  const { findById: findContactList } = useContactLists();

  useEffect(() => {
    findContactList(contactListId).then((data) => {
      setContactList(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactListId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get(`contact-list-items`, {
            params: { searchParam, pageNumber, contactListId },
          });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, contactListId]);

  useEffect(() => {
    const companyId = user.companyId;
    // const socket = socketManager.GetSocket();

    const onCompanyContactLists = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.record });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.id });
      }

      if (data.action === "reload") {
        dispatch({ type: "LOAD_CONTACTS", payload: data.records });
      }
    }
    socket.on(`company-${companyId}-ContactListItem`, onCompanyContactLists);

    return () => {
      socket.off(`company-${companyId}-ContactListItem`, onCompanyContactLists);
    };
  }, [contactListId]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(true);
  };

  const handleCloseContactListItemModal = () => {
    setSelectedContactId(null);
    setContactListItemModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactListItemModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contact-list-items/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleImportContacts = async () => {
    try {
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);
      await api.request({
        url: `contact-lists/${contactListId}/upload`,
        method: "POST",
        data: formData,
      });
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const goToAutomation = () => {
    history.push("/automation");
  };

  return (
    <div className={classes.mainContainer}>
      <ContactListItemModal
        open={contactListItemModalOpen}
        onClose={handleCloseContactListItemModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactListItemModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contactListItems.confirmationModal.deleteTitle")} ${deletingContact.name
            }?`
            : `${i18n.t("contactListItems.confirmationModal.importTitlte")}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleImportContacts()
        }
      >
        {deletingContact ? (
          `${i18n.t("contactListItems.confirmationModal.deleteMessage")}`
        ) : (
          <>
            {i18n.t("contactListItems.confirmationModal.importMessage")}
            <a href={planilhaExemplo} download="planilha.xlsx">
              Clique aqui para baixar planilha exemplo.
            </a>
          </>
        )}
      </ConfirmationModal>
      {
        user.profile === "user" ?
          <ForbiddenPage />
          :
          <>
            <div className={classes.header}>
              <div className={classes.titleContainer}>
                <Typography className={classes.title}>
                  {contactList.name}
                </Typography>
                <Typography className={classes.subtitle}>
                  Gerencie os contatos da lista
                </Typography>
              </div>

              <div className={classes.headerActions}>
                <div className={classes.searchContainer}>
                  <SearchIcon className={classes.searchIcon} />
                  <InputBase
                    placeholder={i18n.t("contactListItems.searchPlaceholder")}
                    classes={{
                      root: classes.searchInput,
                    }}
                    value={searchParam}
                    onChange={handleSearch}
                  />
                </div>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={goToAutomation}
                  className={classes.addButton}
                  startIcon={<ArrowBackIcon />}
                >
                  {"Voltar"}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    fileUploadRef.current.value = null;
                    fileUploadRef.current.click();
                  }}
                  className={classes.addButton}
                  startIcon={<CloudUploadIcon />}
                >
                  {i18n.t("contactListItems.buttons.import")}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenContactListItemModal}
                  className={classes.addButton}
                  startIcon={<AddIcon />}
                >
                  {i18n.t("contactListItems.buttons.add")}
                </Button>
              </div>
            </div>
            <Paper
              className={classes.mainPaper}
              elevation={0}
              onScroll={handleScroll}
            >
              <>
                <input
                  style={{ display: "none" }}
                  id="upload"
                  name="file"
                  type="file"
                  accept=".xls,.xlsx"
                  onChange={() => {
                    setConfirmOpen(true);
                  }}
                  ref={fileUploadRef}
                />
              </>
              <Grid container spacing={3}>
                {contacts.map((contact) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={contact.id}>
                    <Card
                      sx={{
                        position: "relative",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: "16px",
                        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
                        transition: "all 0.3s ease",
                        border: "1px solid",
                        borderColor: "divider",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Box display="flex" alignItems="center" mb={2}>
                          <Avatar
                            sx={{
                              width: 48,
                              height: 48,
                              mr: 2,
                              bgcolor: contact.isWhatsappValid ? "primary.main" : "grey.400",
                            }}
                          >
                            {contact.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ overflow: "hidden" }}>
                            <Typography variant="h6" fontWeight="bold" noWrap title={contact.name}>
                              {contact.name}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {contact.isWhatsappValid ? (
                                <CheckCircleIcon
                                  style={{ fontSize: "1rem", color: "green" }}
                                  titleAccess="Whatsapp Válido"
                                />
                              ) : (
                                <BlockIcon
                                  style={{ fontSize: "1rem", color: "grey" }}
                                  titleAccess="Whatsapp Inválido"
                                />
                              )}
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {contact.isWhatsappValid ? "Válido" : "Inválido"}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <WhatsApp style={{ fontSize: "1rem", color: "#25D366" }} />
                          <Typography variant="body2" color="textSecondary" noWrap>
                            {contact.number}
                          </Typography>
                        </Box>

                        {contact.email && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Email style={{ fontSize: "1rem", color: "rgba(0, 0, 0, 0.54)" }} />
                            <Typography variant="body2" color="textSecondary" noWrap title={contact.email}>
                              {contact.email}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>

                      <Divider />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          p: 1.5,
                          bgcolor: "background.default",
                          gap: 1
                        }}
                      >
                        <Tooltip title="Editar" arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => hadleEditContact(contact.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Can
                          role={user.profile}
                          perform="contacts-page:deleteContact"
                          yes={() => (
                            <Tooltip title="Excluir" arrow>
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => {
                                  setConfirmOpen(true);
                                  setDeletingContact(contact);
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        />
                      </Box>
                    </Card>
                  </Grid>
                ))}
                {loading && (
                  Array.from(new Array(4)).map((_, i) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                      <Card sx={{ height: 180, borderRadius: "16px", p: 2 }}>
                        <Box display="flex" alignItems="center">
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              backgroundColor: "rgba(0,0,0,0.1)",
                              marginRight: 2
                            }}
                          />
                          <Box
                            sx={{
                              width: "60%",
                              height: 20,
                              backgroundColor: "rgba(0,0,0,0.1)",
                              borderRadius: 1
                            }}
                          />
                        </Box>
                      </Card>
                    </Grid>
                  ))
                )}
              </Grid>
            </Paper>
          </>}
    </div>
  );
};

export default ContactListItems;
